import { type FC, memo, type ReactNode, useCallback } from "react";
import { NumberFormatter as NF } from "../../utils/NumberFormatter";
import { NumericFormat } from "react-number-format";

import parse from "html-react-parser";
import { TextField, type TextFieldProps } from "@mui/material";

export interface CustomNumberFormatProps extends Omit<TextFieldProps<"outlined">, "type" | "variant"> {
  value: number | undefined | null;
  label?: ReactNode;
  helperText?: ReactNode;
  error?: boolean;
  disabled?: boolean;
  id?: string;
  allowedLength?: number;

  handleState: (state: NumberFormatEvent) => void;
  decimalScale?: number; // Max amount of digits to the right of decimal point
  max?: string | number;
  maxLength?: number;
  fullWidth?: boolean;
  size?: "small" | "medium";
  defaultValue?: number | undefined | null;
}

export interface NumberFormatEvent {
  floatValue?: number;
  value?: string;
  formattedValue?: string;
}

const CustomNumberFormat: FC<CustomNumberFormatProps> = ({
  value,
  handleState,
  id,
  allowedLength,
  onFocus,
  label,
  className,
  fullWidth = true,
  ...remainingProps
}) => {
  const handleStateCallback = useCallback(
    (evt: NumberFormatEvent) => {
      if (evt.floatValue === value || (value === null && evt.floatValue === undefined)) {
        return;
      }
      handleState(evt);
    },
    [value, handleState],
  );

  const isAllowedCallback = useCallback(
    (event: NumberFormatEvent) => {
      if (!allowedLength || !event.value) {
        return true;
      }

      return event.value.toString().length <= allowedLength;
    },
    [allowedLength],
  );

  return (
    <NumericFormat
      /* ignore invalid typing on value. Null is a correct value */
      value={value ?? ""}
      id={id}
      customInput={TextField}
      thousandSeparator={NF.getThousandSeparator() || false}
      decimalSeparator={NF.getDecimalSeparator() ?? undefined}
      onValueChange={handleStateCallback}
      isAllowed={isAllowedCallback}
      onFocus={onFocus}
      label={typeof label === "string" ? parse(label) : label}
      classes={{
        root: className,
      }}
      fullWidth={fullWidth}
      {...remainingProps}
      variant={"outlined"}
    />
  );
};

export default memo(CustomNumberFormat);
